import { useState, useEffect } from 'react';
import { getAgenteCommissioni } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Search, Euro, Building2, Receipt, CheckCircle, Clock, FileText, ExternalLink } from 'lucide-react';

const CommissioniPage = () => {
  const [commissioni, setCommissioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadCommissioni();
  }, []);

  const loadCommissioni = async () => {
    try {
      const response = await getAgenteCommissioni();
      setCommissioni(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento commissioni');
    } finally {
      setLoading(false);
    }
  };

  const filteredCommissioni = commissioni.filter(c => 
    c.ragione_sociale?.toLowerCase().includes(search.toLowerCase()) ||
    c.id_azienda?.toLowerCase().includes(search.toLowerCase()) ||
    c.id_ordine?.toLowerCase().includes(search.toLowerCase())
  );

  const totaleMaturate = commissioni.reduce((acc, c) => acc + (c.importo_commissione || 0), 0);
  const totalePagate = commissioni.filter(c => c.stato === 'pagata').reduce((acc, c) => acc + (c.importo_commissione || 0), 0);
  const totaleDaPagare = totaleMaturate - totalePagate;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="commissioni-page">
      {/* Sezione Documentazione */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Documentazione Agenti
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Consulta guide, procedure e materiali formativi
                </p>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              onClick={() => window.open('https://drive.google.com/drive/folders/1XYlCZG09fNXFMJOAdtxF3nix3jnVd3wL?usp=sharing', '_blank')}
              data-testid="documentation-link"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Apri Documentazione
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="mb-8">
        <h1 
          className="text-4xl font-semibold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Commissioni
        </h1>
        <p className="text-slate-500 mt-1">Riepilogo commissioni maturate</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card data-testid="totale-maturate">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Totale Maturate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Euro className="h-5 w-5 text-slate-600" />
              </div>
              <span className="text-3xl font-bold text-slate-900">
                {totaleMaturate.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200" data-testid="totale-pagate">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Pagate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-3xl font-bold text-green-600">
                {totalePagate.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200" data-testid="totale-da-pagare">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Da Pagare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-3xl font-bold text-amber-600">
                {totaleDaPagare.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per azienda o ordine..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-commissioni"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {commissioni.length === 0 ? (
            <div className="p-8 text-center">
              <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna commissione presente</p>
              <p className="text-sm text-slate-400 mt-1">Le commissioni appariranno qui quando le tue aziende effettueranno ordini</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="commissioni-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Azienda</th>
                    <th className="text-center p-4 font-medium text-slate-600">Ordine</th>
                    <th className="text-center p-4 font-medium text-slate-600">Importo Ordine</th>
                    <th className="text-center p-4 font-medium text-slate-600">%</th>
                    <th className="text-center p-4 font-medium text-slate-600">Commissione</th>
                    <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                    <th className="text-center p-4 font-medium text-slate-600">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCommissioni.map((comm, idx) => (
                    <tr 
                      key={comm.id || idx} 
                      className="border-b hover:bg-slate-50 transition-colors"
                      data-testid={`commissione-row-${comm.id || idx}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-slate-100">
                            <Building2 className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{comm.ragione_sociale}</p>
                            <p className="text-xs text-slate-500">{comm.id_azienda}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-mono text-sm">{comm.id_ordine}</span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Euro className="h-4 w-4 text-slate-400" />
                          <span>{comm.importo_ordine?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="outline">{comm.percentuale}%</Badge>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1 font-semibold text-green-600">
                          <Euro className="h-4 w-4" />
                          <span>{comm.importo_commissione?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {comm.stato === 'pagata' ? (
                          <Badge className="bg-green-100 text-green-700">Pagata</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Maturata</Badge>
                        )}
                      </td>
                      <td className="p-4 text-center text-slate-500">
                        {comm.data ? new Date(comm.data).toLocaleDateString('it-IT') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommissioniPage;
